import { DATES } from "./config";

// Pure helpers for presenting confirmations, shared by the admin dialog
// and the PDF report.

export function extractNameFromNotes(notes = "") {
  const match = notes.match(/Name:\s*(.+?)\s{2,}/i);
  return match ? match[1].trim() : "Unknown";
}

export function getConfirmationName(conf) {
  if (conf.payer?.name) {
    return `${conf.payer.name.given_name} ${conf.payer.name.surname}`;
  }
  return extractNameFromNotes(conf.notes);
}

export function unitFromNotes(notes) {
  const match = notes?.match(/Unit:\s*([^\s]+)/);
  const unit = match?.[1];
  return unit && unit !== "-" && !unit.includes("undefined") ? unit : "";
}

export function confirmationUnit(conf) {
  return conf.ownerBuilding
    ? `${conf.ownerBuilding}-${conf.ownerUnit}`
    : unitFromNotes(conf.notes);
}

export function confirmationAmount(conf) {
  return conf.payer ? conf.purchase_units?.[0]?.amount?.value : conf.total;
}

// Which invoice a booking belongs to. Confirmations saved before rounds
// existed are round 1.
export function confirmationRound(conf) {
  return conf?.round || 1;
}

export function shortDayLabel(dateString) {
  const date = new Date(dateString);
  const weekday = new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    timeZone: "UTC",
  }).format(date);
  return `${weekday} ${date.getUTCMonth() + 1}/${date.getUTCDate()}`;
}

// "Fri 9/4 × 2, Sat 9/5 × 1" — only dates that actually have sets.
export function describeCartSets(cart) {
  if (!cart?.dates || !cart?.sets) return "";
  return cart.dates
    .map((dateString, i) => ({ dateString, count: cart.sets[i] || 0 }))
    .filter(({ count }) => count > 0)
    .map(({ dateString, count }) => `${shortDayLabel(dateString)} × ${count}`)
    .join(", ");
}

// Sets booked per person for one date, aggregated across that person's
// confirmations (someone with two bookings shows as one pool entry).
export function buildDayPool(confirmations, date) {
  const byOwner = new Map();
  confirmations
    .filter((conf) => conf.status !== "CANCELLED")
    .forEach((conf) => {
      const idx = conf.cart?.dates?.indexOf(date) ?? -1;
      const count = idx >= 0 ? conf.cart?.sets?.[idx] || 0 : 0;
      if (count <= 0) return;
      const name = getConfirmationName(conf);
      const key = conf.uid || name;
      const existing = byOwner.get(key);
      if (existing) {
        existing.total += count;
      } else {
        byOwner.set(key, {
          key,
          name,
          unit: confirmationUnit(conf),
          total: count,
        });
      }
    });
  return [...byOwner.values()].sort((a, b) => a.name.localeCompare(b.name));
}

// Room number by pool key, so a spot on the map can be labelled with the
// unit the beach service puts on the tag.
export function buildUnitLookup(confirmations) {
  const units = {};
  confirmations.forEach((conf) => {
    const key = conf.uid || getConfirmationName(conf);
    const unit = confirmationUnit(conf);
    if (unit && !units[key]) units[key] = unit;
  });
  return units;
}

// Pool entries with their placed sets subtracted; people who have all
// their sets on the map drop out of the list entirely.
export function poolRemaining(pool, dayAssignments) {
  const placed = {};
  Object.values(dayAssignments || {}).forEach((assignment) => {
    if (assignment?.key) {
      placed[assignment.key] = (placed[assignment.key] || 0) + 1;
    }
  });
  return pool
    .map((entry) => ({
      ...entry,
      remaining: Math.max(0, entry.total - (placed[entry.key] || 0)),
    }))
    .filter((entry) => entry.remaining > 0);
}

// Table data for the booking report: one row per non-cancelled
// confirmation (sorted by name) with per-event-date counts, plus
// per-day and dollar totals.
export function buildBookingRows(confirmations) {
  const active = confirmations.filter((c) => c.status !== "CANCELLED");
  const dateTotals = DATES.map(() => 0);
  let grandTotal = 0;

  const rows = active.map((conf) => {
    const counts = DATES.map((date) => {
      const idx = conf.cart?.dates?.indexOf(date) ?? -1;
      return idx >= 0 ? conf.cart?.sets?.[idx] || 0 : 0;
    });
    counts.forEach((count, i) => {
      dateTotals[i] += count;
    });
    const amount = confirmationAmount(conf);
    if (amount != null) grandTotal += parseFloat(amount) || 0;
    return {
      name: getConfirmationName(conf),
      unit: confirmationUnit(conf),
      counts,
      amount,
      status: conf.status,
      round: confirmationRound(conf),
    };
  });
  rows.sort((a, b) => a.name.localeCompare(b.name));

  return { rows, dateTotals, grandTotal, count: active.length };
}
